import { useElementSize } from '@fullstory/react-hooks';
import { classNames } from '@fullstory/util/fmt';
import React, { PropsWithChildren, useMemo, useState } from 'react';
import { Button } from '../../components/Button';
import { Popover } from '../../components/Popover';
import { FSMaskProp } from '../../types';
import { identifyComponent } from '../../util';
import { Box } from '../Box';
import { Option } from '../Option';
import { OptionGroupHeader } from '../OptionGroupHeader';
import { Options } from '../Options';
import { SelectContext } from './Context';
import css from './index.module.scss';

export interface SelectProps
  extends Pick<
      React.ComponentProps<typeof Button>,
      | 'id'
      | 'variant'
      | 'size'
      | 'fullWidth'
      | 'iconLeft'
      | 'iconRight'
      | 'justifyContent'
      | 'disabled'
    >,
    Pick<
      React.ComponentProps<typeof Popover>,
      'onFirstUpdate' | 'flipY' | 'minWidth' | 'maxHeight' | 'preventOverflow'
    > {
  /**
   * For element identity purpose. Default to 'select'
   */
  testId?: string;
  /** Text displayed on the button */
  displayName: string;
  /** An optional display item to show to the right of the displayName */
  rightAdornment?: React.ReactNode;
  /** If `true`, the portal is visible */
  open: boolean;
  /** Function called when portal is opened */
  onOpen: () => void;
  /** Function called when portal is closed */
  onClose: () => void;
  /** Alignment of the options menu below the anchor */
  placement?: 'bottom-start' | 'bottom' | 'bottom-end' | 'top' | 'top-start' | 'top-end';
  /**
   * When true, multiple options can be selected
   */
  multiple?: boolean;

  /**
   * Sets the max-width on the dialog content.
   *
   * TODO(jcarroll2007): This is a bit of a weird prop because it sets the maxwidth. In the case of the OmnisearchKindInput
   * we really want to set `width` and not `maxWidth`. This could be improved.
   */
  contentWidth?: 'auto' | 300 | 600;
  /**
   * The css classname that will be passed to the content element.
   */
  contentClassName?: string;
  /**
   * The css classname that will be passed to the content element.
   */
  buttonClassName?: string;
  /**
   * FS recording masking options. Applies to the button text and the menu popover.
   */
  fsMask?: FSMaskProp;
  /**
   * If provided, determines whether the menu should be rendered into a portal
   */
  portal?: boolean;
}

/**
 * A select is a button that opens it's children in a portal positioned below.
 */
const Select: React.FC<PropsWithChildren<SelectProps>> = ({
  id,
  testId = 'select',
  displayName,
  rightAdornment,
  open,
  onOpen,
  onClose,
  variant,
  size,
  fullWidth,
  justifyContent,
  disabled = false,
  iconLeft,
  iconRight = 'chevron-down',
  maxHeight,
  placement = 'bottom-start',
  onFirstUpdate,
  flipY = false,
  children,
  multiple = false,
  contentWidth,
  minWidth,
  contentClassName,
  buttonClassName,
  fsMask,
  portal = true,
  preventOverflow,
  ...rest
}) => {
  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(null);
  const { elementSize: buttonSize, ref: buttonRef } = useElementSize();
  const ref = (element: HTMLButtonElement | null) => {
    setButtonElement(element);
    buttonRef(element);
  };
  let maxWidth;
  let width;
  // If contentWidth is set, use that as the maxWidth and leave the width undefined.
  // If not, use the default of 300 as the maxWidth, unless fullWidth is set, in which case
  // we want to leave the maxWidth as undefined and set the width to the calculated button width.
  if (contentWidth) {
    maxWidth = contentWidth;
  } else if (fullWidth) {
    width = buttonSize?.width;
  } else {
    maxWidth = 300;
  }
  const selectContext = useMemo(() => ({ multiple }), [multiple]);

  return (
    <SelectContext.Provider value={selectContext}>
      <Button
        data-component="Select"
        {...rest}
        {...identifyComponent('select', rest)}
        id={id}
        testId={`${testId}-button`}
        innerRef={ref}
        size={size}
        variant={variant}
        iconLeft={iconLeft}
        iconRight={iconRight}
        onClick={open ? onClose : onOpen}
        fullWidth={fullWidth}
        justifyContent={justifyContent}
        disabled={disabled}
        fsMask={fsMask}
        aria-haspopup
        className={buttonClassName}
      >
        <Box display="flex" alignItems="center" justifyContent="flex-start" gap={4}>
          <Box overflow="hidden" style={{ textOverflow: 'ellipsis' }}>
            {displayName}
          </Box>
          {rightAdornment && (
            <Box className={css.rightAdornment} flexShrink={0}>
              {rightAdornment}
            </Box>
          )}
        </Box>
      </Button>
      <Popover
        data-component="Select"
        open={open}
        onCloseSignal={onClose}
        anchor={buttonElement}
        offset={8}
        placement={placement}
        testId={`${testId}-portal`}
        maxHeight={maxHeight}
        maxWidth={maxWidth}
        width={width}
        minWidth={minWidth}
        onFirstUpdate={onFirstUpdate}
        flipY={flipY}
        variant="bordered"
        portal={portal}
        preventOverflow={preventOverflow}
      >
        <Box className={classNames(css.scrollable, contentClassName)} fsMask={fsMask}>
          {children}
        </Box>
      </Popover>
    </SelectContext.Provider>
  );
};

const SelectWithStatics = Select as typeof Select & {
  Options: typeof Options;
  Option: typeof Option;
  OptionGroupHeader: typeof OptionGroupHeader;
};
SelectWithStatics.Option = Option;
SelectWithStatics.Options = Options;
SelectWithStatics.OptionGroupHeader = OptionGroupHeader;

export {
  /**
   * Originally, `Select as SelectWithoutStatics` was not exported from this file,
   * but the SelectWithStatics (exported as Select) was causing issues with storybook.
   * It's a little weird at first seeing that they're flipped here, but it's necessary.
   *
   * SelectWithStatics is the Select component with the static pointers to the option related components.
   * (Hence the name SelectWithStatics). It is exported as Select because consumers of this component should
   * only ever need to use it.
   */
  SelectWithStatics as Select,
  Select as SelectWithoutStatics,
};

export { useSelectContext } from './Context';
